/**
 * permissions.js — Single source of truth for Role-Based Access Control.
 *
 * Two roles exist:
 *   admin – full access to everything (except HIDDEN_ROUTES, blocked for all)
 *   user  – only the routes listed in USER_ALLOWED_ROUTES (+ Home "/")
 *
 * ─── How to manage permissions ────────────────────────────────────────
 *
 *  • To GRANT a route to a User:         add its path to USER_ALLOWED_ROUTES.
 *  • To RESTRICT a route to Admin only:  add its path to ADMIN_ONLY_ROUTES.
 *  • Admin always sees everything — no config needed.
 *
 * No other code changes are needed — App.js reads these lists automatically.
 * ──────────────────────────────────────────────────────────────────────
 */

// Routes that are hidden from clients entirely (UI menu, route, and the
// corresponding backend endpoints are all blocked). Set the env var
// REACT_APP_SHOW_INTERNAL_PAGES=true at build time to re-enable them
// for an internal/dev build. The matching backend gate lives in each
// FastAPI service (HIDE_INTERNAL_PAGES env var, default true).
const MULTI_AGENT_ROUTE = '/multi_agent';

const _internalPagesEnabled =
  String(process.env.REACT_APP_SHOW_INTERNAL_PAGES || '').toLowerCase() === 'true';

export const HIDDEN_ROUTES = _internalPagesEnabled
  ? []
  : [
      MULTI_AGENT_ROUTE,         // Home (multi-agent landing)
      '/manager/chat',        // Manager Chat
      '/manager/history',     // Manager Q&A History
      '/knowledge/chat',      // Knowledge Chat
      '/knowledge/history',   // Knowledge Q&A History
      '/knowledge/status',    // Knowledge Base
    ];

// Routes that require Admin role — Users cannot access these.
export const ADMIN_ONLY_ROUTES = [
  MULTI_AGENT_ROUTE,
  '/manager/chat',
  '/knowledge/chat',
  '/admin/users',
  // NOTE: '/admin/org-structure' is intentionally NOT admin-only — any org
  // member may open it to view their own subtree (read-only). Edit controls
  // and write endpoints are gated by org-position (admin or group manager).
  // Menu visibility is gated on `isOrgMember` in App.js.
  '/admin/settings/users',
  '/admin/settings/dashboard-layouts',
];

// Routes open to ALL authenticated users.
// Home ("/") is always accessible and does not need to be listed.
export const USER_ALLOWED_ROUTES = [
  // My Dashboard — personal landing page, open to every authenticated user.
  '/my-dashboard',
  // DISCOVERY — Individual + dynamic tier discoveries are open to all (handled
  // by the '/discovery/' prefix in canAccess). The legacy roster-wide Team /
  // Organization discoveries are admin-only (see canAccess); members use their
  // tier discoveries instead.
  '/discovery/individual',
  // Org Structure page — any authenticated user may open it; the page and the
  // backend scope what they actually see/edit by org position.
  '/admin/org-structure',
];

// Discovery: the legacy roster-wide Team / Organization discoveries are
// admin-only (members use their org-position tier discoveries instead; the
// backend independently confines non-admins to their own subtree). Every
// other Discovery surface — Individual + the dynamic per-tier discoveries
// at /discovery/tier/<level> — is open to all authenticated roles.
//
// Returns the access decision for a Discovery route, or `null` when `path`
// isn't one — the caller (canAccess) then continues its own checks.
export function discoveryRouteDecision(r, path) {
  if (path === '/discovery/org' || path === '/discovery/team') {return r === 'admin';}
  if (path && path.startsWith('/discovery/')) {return true;}
  return null;
}

/**
 * Check whether a given role can access a route path.
 *
 * @param {'admin'|'user'} role  – lowercase role string
 * @param {string}         path  – route path, e.g. '/data/center'
 * @returns {boolean}
 */
export function canAccess(role, path) {
  const r = (role || '').toLowerCase();

  // Routes hidden from clients are blocked for everyone, including admin.
  if (HIDDEN_ROUTES.includes(path)) {return false;}

  const discoveryDecision = discoveryRouteDecision(r, path);
  if (discoveryDecision !== null) {return discoveryDecision;}

  // Home (/multi_agent) requires admin
  if (path === MULTI_AGENT_ROUTE) {return r === 'admin';}

  // Admin can access everything
  if (r === 'admin') {return true;}

  // Admin-only routes — block everyone else
  if (ADMIN_ONLY_ROUTES.includes(path)) {return false;}

  // User role — only USER_ALLOWED_ROUTES
  // Default deny for anything else
  return USER_ALLOWED_ROUTES.includes(path);
}

/**
 * Returns the minimum role required for a route.
 * Useful for filtering menu items by role.
 *
 * @param {string} path – route path
 * @returns {'admin'|'all'}
 */
export function getRouteMinRole(path) {
  if (ADMIN_ONLY_ROUTES.includes(path)) {return 'admin';}
  return 'all';
}
